// The venue Library screen's server state (spec/13 §9.1, F-CAT-02), as TanStack Query hooks over the
// generated client — `packages/contracts/src/routes/library.ts`:
//
//   GET    …/venues/{v}/library            the venue's library                     library:read
//   POST   …/venues/{v}/library            add a catalogue game                    library:manage
//   PATCH  …/venues/{v}/library/{entryId}  edit an entry                           library:manage
//   DELETE …/venues/{v}/library/{entryId}  remove an entry                         library:manage
//   GET    …/venues/{v}/catalogue          browse the platform catalogue to add    library:read
//   GET    …/venues/{v}/library/installs   the titles × stations matrix            library:read     (F-CAT-03)
//   GET    …/venues/{v}/library/detected   found on the PCs, not in the library    library:read     (F-CAT-03)
//   POST   …/venues/{v}/library/rescan     ask stations to rescan now              stations:manage  (F-CAT-03)
//   POST   …/venues/{v}/stations/{s}/apps/{entryId}/install  install one app on one PC        library:manage  (ADR-0056)
//   POST   …/venues/{v}/library/{entryId}/install            …on every PC missing it          library:manage  (ADR-0056)
//   GET    …/venues/{v}/custom-apps        the org's custom apps, marked if offered  library:read     (spec/13 §9)
//   POST   /v1/organizations/{o}/custom-apps (+ PATCH, archive, icon)  the org's custom apps  library:manage
//
// The library is a venue's own selection from the platform catalogue (HQ's `game`, F-CAT-01). The
// catalogue browse is paginated with an optional name search; adding, editing and removing an entry
// re-read the library so the list reflects the change.

import type {
  CatalogueRequest,
  CreateCatalogueRequest,
  AddLibraryEntry,
  ApiClient,
  CatalogueKind,
  CreateCustomApp,
  CustomApp,
  IconContentType,
  IdempotencyKey,
  LibraryEntry,
  LibraryEntryInstall,
  LibraryEntryList,
  LibraryRescan,
  LibraryRescanRequest,
  StationCommandView,
  UpdateCustomApp,
  UpdateLibraryEntry,
  VenueCatalogueGameList,
  VenueCustomAppList,
} from '@clavian/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';
import { iconFileProblem, IconUploadError } from '../../hq/catalogue/icon-upload';
import { type PutWithProgress, xhrPut } from '../../hq/catalogue/trailer-upload';

/** The api's maximum page. A venue library is dozens to a few hundred titles. */
const PAGE_SIZE = 100;

export const libraryKeys = {
  entries: (organizationId: string, venueId: string) => ['library', organizationId, venueId, 'entries'] as const,
  catalogue: (organizationId: string, venueId: string, q: string) => ['library', organizationId, venueId, 'catalogue', q] as const,
  installs: (organizationId: string, venueId: string) => ['library', organizationId, venueId, 'installs'] as const,
  detected: (organizationId: string, venueId: string) => ['library', organizationId, venueId, 'detected'] as const,
  customApps: (organizationId: string, venueId: string) => ['library', organizationId, venueId, 'custom-apps'] as const,
};

/** The titles × stations install matrix (F-CAT-03): every station's last report, in one read. */
export function useInstallMatrix(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: libraryKeys.installs(organizationId, venueId),
    queryFn: () => api.getLibraryInstallMatrix({ path: { organizationId, venueId } }),
    enabled,
  });
}

/** What the venue's PCs have that the library does not (F-CAT-03 "Detected, not in library"). */
export function useDetectedTitles(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: libraryKeys.detected(organizationId, venueId),
    queryFn: () => api.listDetectedTitles({ path: { organizationId, venueId } }),
    enabled,
  });
}

/**
 * Ask stations to rescan what is installed now (`station.library.rescan`, F-CAT-03): the ones named, or every
 * station of the venue. The stations report within seconds, so the install reads are refetched shortly after.
 */
export function useRescanInstalls(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<LibraryRescan, unknown, LibraryRescanRequest>({
    mutationFn: (body) => api.rescanLibraryInstalls({ path: { organizationId, venueId }, body }),
    onSuccess: () => {
      const refetch = () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: libraryKeys.installs(organizationId, venueId) }),
          queryClient.invalidateQueries({ queryKey: libraryKeys.detected(organizationId, venueId) }),
          queryClient.invalidateQueries({ queryKey: ['floor', organizationId, venueId, 'installed'] }),
        ]);
      void refetch();
      for (const delay of RESCAN_REFETCH_MS) setTimeout(() => void refetch(), delay);
    },
  });
}

/** After an install request, re-read where the app stands: the matrix, the library card's lines and the station sheets. */
function refetchInstallsSoon(queryClient: ReturnType<typeof useQueryClient>, organizationId: string, venueId: string): void {
  const refetch = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: libraryKeys.installs(organizationId, venueId) }),
      queryClient.invalidateQueries({ queryKey: ['floor', organizationId, venueId, 'installed'] }),
    ]);
  void refetch();
  for (const delay of RESCAN_REFETCH_MS) setTimeout(() => void refetch(), delay);
}

/**
 * Install one app on one PC now (ADR-0056, `station.app.install`): it installs as soon as the PC is free, never during a
 * session. A fresh `Idempotency-Key` per click, so a double-click asks once.
 */
export function useInstallStationApp(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<StationCommandView, unknown, { readonly stationId: string; readonly entryId: string }>({
    mutationFn: ({ stationId, entryId }) =>
      api.installStationApp({ path: { organizationId, venueId, stationId, entryId }, headerParams: { 'idempotency-key': crypto.randomUUID() as IdempotencyKey } }),
    onSuccess: () => refetchInstallsSoon(queryClient, organizationId, venueId),
  });
}

/** Install one app on every PC of the venue missing it (ADR-0056): answers which PCs were asked, and which were not. */
export function useInstallLibraryEntry(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<LibraryEntryInstall, unknown, { readonly entryId: string }>({
    mutationFn: ({ entryId }) =>
      api.installLibraryEntry({ path: { organizationId, venueId, entryId }, headerParams: { 'idempotency-key': crypto.randomUUID() as IdempotencyKey } }),
    onSuccess: () => refetchInstallsSoon(queryClient, organizationId, venueId),
  });
}

/** Ask HQ to add a detected title the catalogue does not hold (F-CAT-08). Idempotent per title. */
export function useCreateCatalogueRequest(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation<CatalogueRequest, unknown, CreateCatalogueRequest>({
    mutationFn: (body) => api.createCatalogueRequest({ path: { organizationId, venueId }, body }),
  });
}

/** When to re-read after a rescan: the station acks at once and reports within seconds. */
const RESCAN_REFETCH_MS = [3_000, 10_000] as const;

/** The venue's library entries, each carrying its catalogue game. */
export function useLibraryEntries(organizationId: string, venueId: string) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: libraryKeys.entries(organizationId, venueId),
    queryFn: ({ pageParam }): Promise<LibraryEntryList> =>
      api.listLibraryEntries({ path: { organizationId, venueId }, query: { limit: PAGE_SIZE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** The platform catalogue to add from, filtered by `q`, each game flagged whether the venue offers it. */
export function useBrowseCatalogue(organizationId: string, venueId: string, q: string, kind?: CatalogueKind) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [...libraryKeys.catalogue(organizationId, venueId, q), kind ?? 'all'],
    queryFn: ({ pageParam }): Promise<VenueCatalogueGameList> =>
      api.browseVenueCatalogue({
        path: { organizationId, venueId },
        query: {
          limit: PAGE_SIZE,
          ...(q === '' ? {} : { q }),
          ...(kind === undefined ? {} : { kind }),
          ...(pageParam === undefined ? {} : { cursor: pageParam }),
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** Add a catalogue game to the venue library. Re-reads the library and the catalogue browse on success. */
export function useAddLibraryEntry(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<LibraryEntry, unknown, AddLibraryEntry>({
    mutationFn: (body) => api.addLibraryEntry({ path: { organizationId, venueId }, body }),
    onSuccess: () => void invalidate(queryClient, organizationId, venueId),
  });
}

/** Edit an entry — its category, classes, order, featured flag or visibility. */
export function useUpdateLibraryEntry(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<LibraryEntry, unknown, { entryId: string; body: UpdateLibraryEntry }>({
    mutationFn: ({ entryId, body }) => api.updateLibraryEntry({ path: { organizationId, venueId, entryId }, body }),
    onSuccess: () => void invalidate(queryClient, organizationId, venueId),
  });
}

/** Remove an entry — this venue stops offering the title; the catalogue game is untouched. */
export function useRemoveLibraryEntry(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, { entryId: string }>({
    mutationFn: ({ entryId }) => api.removeLibraryEntry({ path: { organizationId, venueId, entryId } }),
    onSuccess: () => void invalidate(queryClient, organizationId, venueId),
  });
}

/** Re-read the library and its catalogue-browse (the `inLibrary` flags move) after a write. */
function invalidate(queryClient: ReturnType<typeof useQueryClient>, organizationId: string, venueId: string): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: libraryKeys.entries(organizationId, venueId) }),
    queryClient.invalidateQueries({ queryKey: ['library', organizationId, venueId, 'catalogue'] }),
    queryClient.invalidateQueries({ queryKey: libraryKeys.detected(organizationId, venueId) }),
    queryClient.invalidateQueries({ queryKey: libraryKeys.installs(organizationId, venueId) }),
    queryClient.invalidateQueries({ queryKey: libraryKeys.customApps(organizationId, venueId) }),
  ]);
}

// -------------------------------------------------------------------------------------------------
// The organisation's custom apps (spec/13 §9)
// -------------------------------------------------------------------------------------------------

/** The organisation's unarchived custom apps, each marked whether this venue already offers it. */
export function useVenueCustomApps(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: libraryKeys.customApps(organizationId, venueId),
    queryFn: ({ pageParam }): Promise<VenueCustomAppList> =>
      api.browseVenueCustomApps({ path: { organizationId, venueId }, query: { limit: PAGE_SIZE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
    enabled,
  });
}

/** Create a custom app for the organisation. */
export function useCreateCustomApp(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<CustomApp, unknown, CreateCustomApp>({
    mutationFn: (body) => api.createCustomApp({ path: { organizationId }, body }),
    onSuccess: () => void invalidate(queryClient, organizationId, venueId),
  });
}

/** Edit a custom app; every venue that offers it picks the change up. */
export function useUpdateCustomApp(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<CustomApp, unknown, { customAppId: string; body: UpdateCustomApp }>({
    mutationFn: ({ customAppId, body }) => api.updateCustomApp({ path: { organizationId, customAppId }, body }),
    onSuccess: () => void invalidate(queryClient, organizationId, venueId),
  });
}

/** Archive a custom app: out of the picker and every station's bundle. */
export function useArchiveCustomApp(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<CustomApp, unknown, { customAppId: string }>({
    mutationFn: ({ customAppId }) => api.archiveCustomApp({ path: { organizationId, customAppId } }),
    onSuccess: () => void invalidate(queryClient, organizationId, venueId),
  });
}

/**
 * Reserve, upload and complete one custom app icon — the HQ app icon's three steps (`hq/catalogue/icon-upload.ts`),
 * under the organisation. Throws `IconUploadError` for a refusal before or during the PUT.
 */
export async function uploadCustomAppIcon(
  api: ApiClient,
  organizationId: string,
  customAppId: string,
  file: File,
  put: PutWithProgress = xhrPut,
): Promise<CustomApp> {
  const problem = iconFileProblem(file);
  if (problem !== undefined) throw new IconUploadError(problem);
  const reserved = await api.createCustomAppIconUpload({
    path: { organizationId, customAppId },
    body: { contentType: file.type as IconContentType, bytes: file.size },
  });
  const sent = await put(reserved.upload.url, reserved.upload.contentType, file, () => undefined).catch(() => false);
  if (!sent) throw new IconUploadError('failed');
  return api.completeCustomAppIconUpload({ path: { organizationId, customAppId, uploadId: reserved.uploadId } });
}
